/**
 * TimelineTaskSubmissionPopper Component
 *
 * MUI Popover form for logging a task submission.
 * Anchored at click coordinates (screen-space), portaled outside SVG by MUI.
 *
 * Upserts the task's submissions JSONB field only — does not alter task state.
 */

import React, { useState } from 'react'
import {
  Popover,
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useSnackbar } from 'notistack'
import { useUpdateCropSchedule, useUpdateTask } from '~/api/supabase-mutations'
import { useCropsContextDispatch, cropEdited } from '~/common/context/crops/crops-context'
import { useCurrentUser } from '~/common/use-current-user'
import { addDaysToDate, parseIntervalToDays, toDateInputValue } from '~/common/utils/date-utils'
import { NumberFieldOrSpinner } from '~/components/number-spinner-field'
import { DeleteSubmissionModal } from '../delete-submission-modal'
import { useTimelineInteractionContext } from '../../context/timeline-interaction-context'
import { useMeasurementUnits } from '~/common/use-measurement-units'
import { supabaseGetter } from '~/api/supabase-getter'

/**
 * TimelineTaskSubmissionPopper
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Object} props.task - Transformed task object (has task_window_start, duration, submissions, id)
 * @param {number} props.anchorX - Screen X from click event
 * @param {number} props.anchorY - Screen Y from click event
 * @param {Function} props.onClose
 * @param {Object} [props.submission] - Existing submission to edit; omit for new submission
 */
export const TimelineTaskSubmissionPopper = ({ open, task, anchorX, anchorY, onClose, submission }) => {
  const { updateTask, loading } = useUpdateTask()
  const { enqueueSnackbar } = useSnackbar()
  const cropsDispatch = useCropsContextDispatch()
  const { currentUser } = useCurrentUser()
  const { hoverData } = useTimelineInteractionContext()
  const { massUnit, toDisplayMass, fromDisplayMass } = useMeasurementUnits()
  const { updateSchedule } = useUpdateCropSchedule()

  const today = toDateInputValue(hoverData?.dateStrLong ? new Date(hoverData.dateStrLong) : new Date())
  const windowStart = toDateInputValue(task.task_window_start)
  const durationDays = parseIntervalToDays(task.duration)
  const windowEnd = addDaysToDate(task.task_window_start, durationDays)

  const [selectedDate, setSelectedDate] = useState(
    submission ? toDateInputValue(new Date(submission.submitted_at)) : today
  )
  const [yieldDisplay, setYieldDisplay] = useState(
    submission?.yield_kg != null ? String(toDisplayMass(submission.yield_kg)) : ''
  )
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  const handleDelete = async () => {
    const updatedSubmissions = (task.submissions || []).filter(s => s.id !== submission.id)
    await Promise.all([
      updateTask(task.id, { submissions: updatedSubmissions }),
      (async () => {
        try {
          const { data: schedule, error: scheduleError } = await supabaseGetter.cropSchedules.getActiveByCrop(task.crop_id)
          if (scheduleError) throw scheduleError
          if (!schedule) throw new Error('No schedule found for crop')
          const newYield = (schedule.yield ?? 0) - (submission.yield_kg ?? 0)
          await updateSchedule(schedule.id, { yield: Math.max(0, newYield) })
        } catch (err) {
          console.error('[TimelineTaskSubmissionPopper] Failed to update schedule yield on delete:', err)
          throw err
        }
      })()
    ]).then(() => {
      supabaseGetter.crops.invalidate()
      cropsDispatch(cropEdited({ cropId: task.crop_id }))
      enqueueSnackbar('Harvest log removed', { variant: 'info' })
      onClose()
    })
  }

  const handleSave = async () => {
    if (!currentUser?.id) return

    const yield_kg = yieldDisplay !== '' ? fromDisplayMass(parseFloat(yieldDisplay)) : null

    const newEntry = {
      id: submission?.id ?? crypto.randomUUID(),
      submitted_by: currentUser.id,
      submitted_at: new Date(selectedDate).toISOString(),
      yield_kg,
    }

    const updatedSubmissions = submission
      ? (task.submissions || []).map(s => s.id === submission.id ? newEntry : s)
      : [...(task.submissions || []), newEntry]

    // update the task and schedule records
    await Promise.all([
      updateTask(task.id, { submissions: updatedSubmissions }),
      // update the crop schedule to accum the yield with the new submission total as a counter cache
      (async () => {
        try {
          const { data: schedule, error: scheduleError } = await supabaseGetter.cropSchedules.getActiveByCrop(task.crop_id)

          if (scheduleError) {
            throw scheduleError
          }
          if (!schedule) {
            throw new Error('No schedule found for crop')
          }

          const prevYield = schedule.yield ?? 0
          // For edits, subtract the old submission's yield before adding the new value
          const oldYield = submission?.yield_kg ?? 0
          const newYield = prevYield - oldYield + (yield_kg ?? 0)
          await updateSchedule(schedule.id, { yield: Math.max(0, newYield) })
        } catch (err) {
          console.error('[TimelineTaskSubmissionPopper] Failed to update schedule yield:', err)
          throw err
        }
      })()
    ]).then(() => {
      // Invalidate crops cache so useCropData re-fetches with the updated schedule yield
      supabaseGetter.crops.invalidate()
      cropsDispatch(cropEdited({ cropId: task.crop_id }))
      enqueueSnackbar(
        submission ? 'Harvest log updated — season yield recalculated' : 'Harvest logged — season yield updated',
        { variant: 'success' }
      )
      onClose?.()
    });

  }

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={{ top: anchorY, left: anchorX }}
      onClose={onClose}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      slotProps={{ paper: { sx: { p: 1, width: 240 } } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          {submission ? 'Edit Harvest Log' : 'Log Harvest'}
        </Typography>
        {submission && <Chip label={toDateInputValue(new Date(submission.submitted_at))} size="small" color="primary" variant="outlined" />}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField
          label="Harvest Date"
          type="date"
          size="small"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          slotProps={{
            htmlInput: { min: windowStart, max: windowEnd },
            inputLabel: { shrink: true },
          }}
          fullWidth
        />

        <NumberFieldOrSpinner
          label={`Yield (${massUnit})`}
          size="small"
          min={0}
          value={yieldDisplay}
          onValueChange={(value) => setYieldDisplay(value)}
        />

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {submission && (
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() => setDeleteModalOpen(true)}
              disabled={loading}
              sx={{ minWidth: 0, px: '5px' }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </Button>
          )}
          <Box sx={{ flex: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant='outlined' size="small" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={loading || !selectedDate}
              startIcon={loading ? <CircularProgress size={14} /> : null}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>

      <DeleteSubmissionModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        loading={loading}
      />
    </Popover>
  )
}
